(() => {
  const imageControllers = new Map();
  const initImages = page => {
    const records = new Map();
    const pause = record => {
      if (record.started !== null) record.elapsed += performance.now() - record.started;
      record.started = null;
      clearTimeout(record.labelTimer);
      clearTimeout(record.slowTimer);
    };
    const settle = (record, state) => {
      const wasPending = record.state === 'pending' || record.state === 'slow';
      pause(record);
      record.state = state;
      record.wrap.dataset.imageState = state === 'ready' && wasPending && record.inView ? 'loaded' : state;
      record.retry.hidden = state !== 'error';
      record.feedback.hidden = state === 'ready';
      record.message.textContent = state === 'error' ? record.feedback.dataset.error : state === 'slow' ? record.feedback.dataset.slow : '';
      if (state === 'ready') observer.unobserve(record.wrap);
    };
    const sync = record => {
      const visible = record.inView && !document.hidden && page.isConnected;
      record.wrap.toggleAttribute('data-image-visible', visible);
      record.message.setAttribute('aria-live', visible ? 'polite' : 'off');
      if (!visible || record.state !== 'pending') { pause(record); return; }
      if (record.started !== null) return;
      record.started = performance.now();
      record.labelTimer = setTimeout(() => {
        if (record.state === 'pending' && page.isConnected) record.message.textContent = record.feedback.dataset.slow;
      }, Math.max(0, 8000 - record.elapsed));
      record.slowTimer = setTimeout(() => {
        if (record.state === 'pending' && page.isConnected) settle(record, 'slow');
      }, Math.max(0, 15000 - record.elapsed));
    };
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      const record = records.get(entry.target);
      if (!record) return;
      record.inView = entry.isIntersecting;
      sync(record);
    }));
    const bind = record => {
      record.onLoad = () => {
        record.wrap.toggleAttribute('data-has-preview', !!window.NorthyardProgressive?.hasPreview(record.img));
        if (record.img.dataset.nyState === 'error') settle(record, 'error');
        else if (record.img.complete && record.img.naturalWidth > 0 && (!window.NorthyardProgressive || window.NorthyardProgressive.isReady(record.img))) settle(record, 'ready');
        else if (record.state === 'ready' || record.state === 'initial' || record.state === 'error') { settle(record, 'pending'); sync(record); }
      };
      record.onError = () => { if ((!record.img.hasAttribute('data-ny-image') || record.img.dataset.nyState === 'native') && !record.img.naturalWidth) settle(record, 'error'); };
      record.img.addEventListener('load', record.onLoad);
      record.img.addEventListener('error', record.onError);
      record.img.addEventListener('northyard:image-state', record.onLoad);
    };
    const scan = () => page.querySelectorAll('[data-search-image]').forEach(wrap => {
      if (records.has(wrap)) return;
      const img = wrap.querySelector('img');
      const feedback = wrap.querySelector('[data-image-feedback]');
      if (!img || !feedback) return;
      const record = { wrap, img, feedback, message: feedback.querySelector('[data-image-message]'), retry: feedback.querySelector('[data-image-retry]'), state: 'initial', inView: false, elapsed: 0, started: null };
      records.set(wrap, record);
      bind(record);
      observer.observe(wrap);
      if (img.hasAttribute('data-ny-image')) record.onLoad();
      else if (img.naturalWidth > 0) settle(record, 'ready');
      else if (img.complete) settle(record, 'error');
      else { settle(record, 'pending'); sync(record); }
      record.onRetry = () => {
        if (record.state !== 'error') return;
        if (document.activeElement === record.retry) {
          const title = record.wrap.closest('.ny-search-card')?.querySelector('h3');
          if (title) { title.tabIndex = -1; title.focus({ preventScroll: true }); }
        }
        const oldImage = record.img;
        if (window.NorthyardProgressive?.retry(oldImage)) { record.elapsed = 0; settle(record, 'pending'); sync(record); return; }
        oldImage.removeEventListener('load', record.onLoad);
        oldImage.removeEventListener('error', record.onError);
        oldImage.removeEventListener('northyard:image-state', record.onLoad);
        record.img = oldImage.cloneNode(false);
        record.elapsed = 0;
        settle(record, 'pending');
        bind(record);
        oldImage.replaceWith(record.img);
        sync(record);
        if (record.img.complete && record.img.naturalWidth > 0) settle(record, 'ready');
      };
      record.retry.addEventListener('click', record.onRetry);
    });
    scan();
    page.addEventListener('northyard:list:append', scan);
    const onVisibility = () => records.forEach(sync);
    document.addEventListener('visibilitychange', onVisibility);
    imageControllers.set(page, () => {
      observer.disconnect();
      page.removeEventListener('northyard:list:append', scan);
      document.removeEventListener('visibilitychange', onVisibility);
      records.forEach(record => {
        pause(record);
        record.img.removeEventListener('load', record.onLoad);
        record.img.removeEventListener('error', record.onError);
        record.img.removeEventListener('northyard:image-state', record.onLoad);
        record.retry.removeEventListener('click', record.onRetry);
        record.wrap.removeAttribute('data-image-state');
        record.wrap.removeAttribute('data-image-visible');
        record.feedback.hidden = true;
      });
      imageControllers.delete(page);
      delete page.dataset.ready;
    });
  };
  const init = (root = document) => root.querySelectorAll('[data-search-page]').forEach(page => {
    if (page.dataset.ready) return;
    page.dataset.ready = 'true';
    initImages(page);
    const query = page.querySelector('[data-search-query]');
    page.querySelector('[data-search-edit]')?.addEventListener('click', event => {
      event.preventDefault();
      query.focus();
      query.select();
    });
    const form = page.querySelector('[data-search-refine]');
    if (form) {
      const min = form.querySelector('[data-price-min]');
      const max = form.querySelector('[data-price-max]');
      const validate = () => {
        if (max) max.setCustomValidity(min?.value && max.value && Number(min.value) > Number(max.value) ? form.dataset.priceError : '');
      };
      form.addEventListener('input', validate);
      form.addEventListener('invalid', event => {
        let disclosure = event.target.closest('details');
        while (disclosure) {
          disclosure.open = true;
          disclosure = disclosure.parentElement?.closest('details');
        }
      }, true);
      form.addEventListener('formdata', event => {
        for (const input of form.querySelectorAll('input[type="number"]')) {
          if (!input.value) event.formData.delete(input.name);
        }
      });
    }
    const suggestions = page.querySelector('[data-search-suggestions]');
    if (!suggestions || suggestions.dataset.query.trim().length < 2) return;
    const url = new URL(suggestions.dataset.suggestUrl, location.origin);
    url.search = new URLSearchParams({ q: suggestions.dataset.query, 'resources[type]': 'query', 'resources[limit]': '3' });
    fetch(url, { headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(4000) })
      .then(response => response.ok ? response.json() : null)
      .then(data => {
        if (!page.isConnected) return;
        const seen = new Set([suggestions.dataset.query.toLocaleLowerCase()]);
        const results = data?.resources?.results?.queries;
        if (!Array.isArray(results)) return;
        const links = suggestions.querySelector('[data-suggestion-links]');
        for (const result of results.slice(0, 3)) {
          if (typeof result.text !== 'string' || !result.text.trim() || seen.has(result.text.toLocaleLowerCase())) continue;
          seen.add(result.text.toLocaleLowerCase());
          const target = new URL(suggestions.dataset.searchUrl, location.origin);
          target.search = new URLSearchParams({ q: result.text, type: 'product', 'options[prefix]': 'last' });
          const link = document.createElement('a');
          link.href = target.href;
          link.textContent = result.text;
          links.append(link);
        }
        suggestions.hidden = !links.childElementCount;
      }).catch(() => {});
  });
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [page, cleanup] of imageControllers) {
      if (event.target.contains(page)) cleanup();
    }
  });
})();
